import type { Phase, SideEvent, TurnEvent } from "../types";

/** Tools that mutate state — their presence marks an execution turn. */
export const WRITE_TOOLS = new Set([
  "Edit",
  "Write",
  "NotebookEdit",
  "Bash",
  "MultiEdit",
  "mcp__ide__executeCode",
]);

/** Read-only tools — exploration when nothing stronger is present. */
export const READ_TOOLS = new Set([
  "Read",
  "Grep",
  "Glob",
  "LS",
  "WebFetch",
  "WebSearch",
  "ToolSearch",
  "TaskOutput",
  "BashOutput",
  "NotebookRead",
  "Monitor",
]);

/** Subagent spawners. 'Task' is the pre-2.1 name, kept for old logs. */
export const DELEGATION_TOOLS = new Set(["Agent", "Workflow", "Task"]);

export const PLANNING_TOOLS = new Set(["ExitPlanMode", "EnterPlanMode", "exit_plan_mode"]);

/** No-tool turns with at least this much thinking are treated as planning. */
export const THINKING_PLANNING_MIN_CHARS = 1500;

/** Odd-sized majority-vote window used to smooth single-turn phase islands. */
export const SMOOTHING_WINDOW = 5;

export interface ClassifiedTurn {
  turn: TurnEvent;
  phase: Phase;
  phaseSmoothed: Phase;
}

interface Span {
  start: number;
  end: number;
}

/**
 * Reconstructs plan-mode intervals per session from plan_mode /
 * plan_mode_exit events. An unclosed span extends to the end of the session.
 */
export function buildPlanSpans(events: SideEvent[]): Map<string, Span[]> {
  const bySession = new Map<string, SideEvent[]>();
  for (const e of events) {
    if (e.kind !== "plan_mode" && e.kind !== "plan_mode_exit") continue;
    if (e.timestamp === undefined) continue;
    const list = bySession.get(e.sessionId);
    if (list) list.push(e);
    else bySession.set(e.sessionId, [e]);
  }
  const spans = new Map<string, Span[]>();
  for (const [sessionId, list] of bySession) {
    list.sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));
    const out: Span[] = [];
    let openStart: number | undefined;
    for (const e of list) {
      if (e.kind === "plan_mode") {
        openStart ??= e.timestamp;
      } else if (openStart !== undefined) {
        out.push({ start: openStart, end: e.timestamp ?? openStart });
        openStart = undefined;
      }
    }
    if (openStart !== undefined) out.push({ start: openStart, end: Infinity });
    spans.set(sessionId, out);
  }
  return spans;
}

export function rawPhase(turn: TurnEvent, inPlanSpan: boolean): Phase {
  if (inPlanSpan || turn.tools.some((t) => PLANNING_TOOLS.has(t))) return "planning";
  if (turn.tools.some((t) => DELEGATION_TOOLS.has(t))) return "delegation";
  if (turn.tools.some((t) => WRITE_TOOLS.has(t))) return "execution";
  if (turn.tools.some((t) => READ_TOOLS.has(t))) return "exploration";
  if (turn.numToolUses === 0 && turn.thinkingChars >= THINKING_PLANNING_MIN_CHARS) {
    return "planning";
  }
  return "chat";
}

/**
 * Classifies every turn, then majority-smooths within each session so that
 * one-off islands (a single Read amid heavy editing) adopt the surrounding
 * phase. Ties keep the raw phase.
 */
export function classifyTurns(turns: TurnEvent[], events: SideEvent[]): ClassifiedTurn[] {
  const spans = buildPlanSpans(events);
  const bySession = new Map<string, TurnEvent[]>();
  for (const t of turns) {
    const list = bySession.get(t.sessionId);
    if (list) list.push(t);
    else bySession.set(t.sessionId, [t]);
  }

  const out: ClassifiedTurn[] = [];
  for (const [sessionId, sessionTurns] of bySession) {
    sessionTurns.sort((a, b) => a.timestamp - b.timestamp || (a.uuid < b.uuid ? -1 : 1));
    const sessionSpans = spans.get(sessionId) ?? [];
    const raw = sessionTurns.map((t) =>
      rawPhase(
        t,
        sessionSpans.some((s) => t.timestamp >= s.start && t.timestamp <= s.end),
      ),
    );
    const smoothed = smoothPhases(raw);
    for (let i = 0; i < sessionTurns.length; i++) {
      out.push({
        turn: sessionTurns[i] as TurnEvent,
        phase: raw[i] as Phase,
        phaseSmoothed: smoothed[i] as Phase,
      });
    }
  }
  return out;
}

export function smoothPhases(raw: Phase[]): Phase[] {
  const half = Math.floor(SMOOTHING_WINDOW / 2);
  return raw.map((phase, i) => {
    const counts = new Map<Phase, number>();
    const lo = Math.max(0, i - half);
    const hi = Math.min(raw.length - 1, i + half);
    for (let j = lo; j <= hi; j++) {
      const p = raw[j] as Phase;
      counts.set(p, (counts.get(p) ?? 0) + 1);
    }
    let maxN = 0;
    for (const n of counts.values()) maxN = Math.max(maxN, n);
    const winners = [...counts.entries()].filter(([, n]) => n === maxN).map(([p]) => p);
    return winners.length === 1 ? (winners[0] as Phase) : phase;
  });
}
